import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { FileDropzone } from "@/components/FileDropzone";
import { api, errMsg, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, FileSpreadsheet, Download } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function ImageToExcel() {
  const { refresh } = useAuth();
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  const [record, setRecord] = useState(null);
  const timer = useRef(null);

  useEffect(() => () => clearInterval(timer.current), []);

  const poll = (jobId) => {
    clearInterval(timer.current);
    timer.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/analysis/${jobId}`);
        setRecord(data);
        if (data.status !== "processing") {
          clearInterval(timer.current);
          setBusy(false);
          if (data.status === "failed") toast.error(data.warnings?.[0] || "No structured table could be detected.");
          else toast.success("Conversion complete");
        }
      } catch (e) { clearInterval(timer.current); setBusy(false); toast.error(errMsg(e)); }
    }, 3000);
  };

  const start = async () => {
    if (!files.length) return toast.error("Add at least one file");
    setBusy(true);
    setRecord(null);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post("/extract/images", fd);
      setJob(data.job_id);
      refresh();
      poll(data.job_id);
    } catch (e) { setBusy(false); toast.error(errMsg(e)); }
  };

  const statuses = {};
  (record?.files || []).forEach((f) => { statuses[f.filename] = f; });
  if (busy) files.forEach((f) => { if (!statuses[f.name]) statuses[f.name] = { status: "Processing" }; });

  return (
    <div className="max-w-6xl space-y-8" data-testid="image-to-excel-page">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Mode A</p>
        <h1 className="mt-2 flex items-center gap-3 font-display text-3xl font-extrabold tracking-tighter">
          <FileSpreadsheet className="h-7 w-7 text-primary" /> Image / PDF → Excel
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Convert tables, invoices, lists and documents into editable Excel spreadsheets. Tables are
          reconstructed with headers, rows and columns — not flat OCR text.
        </p>
      </div>

      <FileDropzone files={files} setFiles={setFiles} statuses={statuses} />

      <div className="flex flex-wrap gap-3">
        <Button onClick={start} disabled={busy} className="rounded-full" data-testid="start-conversion-submit-btn">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Convert to Excel
        </Button>
        {record?.status === "ready" && (
          <Button variant="outline" className="rounded-full bg-white"
                  onClick={() => downloadFile(`/export/excel/${job}`, "GrowRupee_Tables.xlsx")
                    .then(() => toast.success("Excel downloaded")).catch((e) => toast.error(errMsg(e)))}
                  data-testid="download-tables-excel-btn">
            <Download className="mr-2 h-4 w-4" /> Download XLSX
          </Button>
        )}
      </div>

      {record?.warnings?.length > 0 && (
        <div className="rounded-lg border border-warn-border bg-warn p-4 text-sm text-warn-foreground"
             data-testid="conversion-warnings">
          {record.warnings.map((w) => <p key={w}>{w}</p>)}
        </div>
      )}

      {(record?.tables || []).map((t, i) => (
        <div key={i} className="rounded-lg border border-border bg-white" data-testid={`table-preview-${i}`}>
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <h3 className="font-display text-base font-semibold">
              {t.title || `Table ${i + 1}`} <span className="text-xs font-normal text-muted-foreground">
                · {t.source_file} · page {t.page}</span>
            </h3>
            <span className="text-xs text-muted-foreground">Confidence {Math.round((t.confidence || 0.9) * 100)}%</span>
          </div>
          <div className="max-h-96 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>{(t.headers || []).map((h, j) => <TableHead key={j}>{h}</TableHead>)}</TableRow>
              </TableHeader>
              <TableBody>
                {(t.rows || []).slice(0, 50).map((r, ri) => (
                  <TableRow key={ri}>
                    {r.map((c, ci) => <TableCell key={ci} className="text-xs">{c}</TableCell>)}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      ))}
    </div>
  );
}
